<template>
  <v-dialog
    v-model="isWebPushWizardOpen"
    @input="handlePossibleClose"
    max-width="800"
    :activator="null"
  >
    <NotificationActivationWizard
      v-if="isPushSupported && showActivationWizard"
      :already-activated="isPushAlreadyActivated"
      :key="activationWizardKey"
      :wait-for-push="waitForPush"
      :send-test-push="sendTestPushNotification"
      :register-device="registerDevice"
      :is-notification-permission-granted="isNotificationPermissionGranted"
      :request-notification-permission="requestNotificationPermission"
      @close="closeWizard"
    />
    <NotificationPermissionWizard
      v-else-if="isPushSupported && isNotificationPermissionRequired"
      :is-i-o-s="isIOSDevice"
      @requestPermission="handlePermissionRequest"
    />
    <PushUnsupportedWizard
      @close="closeWizard"
      v-else
      :push-blocked="
        (isPushSupported && isNotificationPermissionDenied) ||
        isNotificationPermissionMuted
      "
    />
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue"
import { useNotifications } from "features/push/concerns/notifications"
import NotificationActivationWizard from "features/push/NotificationsActivationWizard.vue"
import PushUnsupportedWizard from "features/push/PushUnsupportedWizard.vue"
import NotificationPermissionWizard from "features/push/NotificationPermissionWizard.vue"
import { isIOS } from "features/push/concerns/platform"
import { AxiosError, AxiosResponse } from "axios"
import { PushRegistration } from "suus-api"
import {
  enablePush,
  sendTestPush,
  waitForTestMessage,
} from "initializers/firebase"

function isSafariNotPWA() {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const isNotPWA = !window.matchMedia("(display-mode: standalone)").matches

  console.log("isSafari", isSafari)
  console.log("isNotPWA", isNotPWA)
  return isSafari && isNotPWA
}

export default defineComponent({
  name: "WebPushDialog",
  components: {
    NotificationPermissionWizard,
    PushUnsupportedWizard,
    NotificationActivationWizard,
  },
  data() {
    return {
      isWebPushWizardOpen: false,
      isPushAlreadyActivated: false,
      showActivationWizard: false,
      activationWizardKey: 0,
    }
  },
  watch: {
    "$route.params": {
      handler() {
        if (window.location.hash === "#push") {
          if (isSafariNotPWA()) {
            window.location.hash = "#pwa-install"
            return
          }
          this.openWizard()
        }
      },
      deep: true,
      immediate: true,
    },
    isWebPushWizardOpen(newVal) {
      window.location.hash = newVal ? "#push" : ""
    },
    isNotificationPermissionGranted(newVal) {
      if (newVal) this.openActivationWizard(false)
    },
    isNotificationPermissionMuted(newVal) {
      if (newVal) this.isNotificationPermissionDenied = true
    },
  },
  methods: {
    handlePossibleClose(value) {
      if (!value) {
        this.closeWizard()
      }
    },
    closeWizard() {
      this.isWebPushWizardOpen = false
      setTimeout(() => {
        this.closeActivationWizard()
      }, 300)
    },
    openWizard() {
      this.reloadNotificationPermissions()
      if (this.isNotificationPermissionGranted) {
        this.openActivationWizard(true)
      }
      this.isWebPushWizardOpen = true
    },
    openActivationWizard(alreadyActivated: boolean) {
      this.isPushAlreadyActivated = alreadyActivated
      this.showActivationWizard = true
      this.activationWizardKey++
    },
    closeActivationWizard() {
      this.showActivationWizard = false
      this.isPushAlreadyActivated = false
    },
    async handlePermissionRequest() {
      await this.requestNotificationPermission()
    },
  },
  setup() {
    const runningAsPwa = window.matchMedia("(display-mode: standalone)").matches

    const isIOSDevice = ref(isIOS())

    const registerDevice = async (repair?: boolean) => {
      try {
        const deviceRegistration: AxiosResponse<PushRegistration> =
          await enablePush(repair)
        return (
          deviceRegistration.status === 200 || deviceRegistration.status === 201
        )
      } catch (e) {
        console.error(e)
        return false
      }
    }

    const messageId = ref("")
    let pushAlreadyReceived = false

    const sendTestPushNotification = async () => {
      let sendTestPushResponse = null
      try {
        waitForTestMessage("", 1000).then(() => {
          pushAlreadyReceived = true
        })
        sendTestPushResponse = await sendTestPush()
        messageId.value = sendTestPushResponse.data.message_id
        return sendTestPushResponse.status === 200
      } catch (e) {
        if (
          (e as AxiosError).isAxiosError &&
          (e as AxiosError).response.status === 410
        ) {
          // 410=gone, token was already deleted from DB after FCM reported it does not exist
          return false
        }
        console.error(e)
        return false
      }
    }

    const waitForPush = async () => {
      // When delivery was so fast, that FCM was actually faster than our backend :0
      if (pushAlreadyReceived) {
        console.log("Push already received, glad we catched it!")
        return true
      }

      try {
        const waitForPushResponse = await waitForTestMessage(
          messageId.value,
          20000
        )
        return waitForPushResponse
      } catch (e) {
        console.error(e)
        return false
      }
    }

    const {
      notificationPermissionStatus,
      isNotificationPermissionGranted,
      isNotificationPermissionRequired,
      isNotificationPermissionDenied,
      isNotificationPermissionMuted,
      requestNotificationPermission,
      reloadNotificationPermissions,
      isPushSupported,
    } = useNotifications()

    return {
      runningAsPwa,
      registerDevice,
      notificationPermissionStatus,
      isNotificationPermissionGranted,
      isNotificationPermissionRequired,
      isNotificationPermissionDenied,
      isNotificationPermissionMuted,
      reloadNotificationPermissions,
      requestNotificationPermission,
      isPushSupported,
      isIOSDevice,
      sendTestPushNotification,
      waitForPush,
    }
  },
  mounted() {
    const pushWizardDismissed: boolean =
      this.$cookies.get("pushWizardDismissed") == "true"
    if (
      this.runningAsPwa &&
      this.isNotificationPermissionRequired &&
      !pushWizardDismissed
    ) {
      setTimeout(() => {
        this.$cookies.set("pushWizardDismissed", "true", "14d", "/")
        this.openWizard()
      }, 3000)
    }
  },
})
</script>

<!-- window.enablePush für registerDevice -->
